import { useTranslation } from 'react-i18next';
import './PitchTypeRanges.scss';

import { PitchGrouping, PitchTypeRangesMeasurementOptions, PitchTypeRangesValueObject } from 'types/CodegenReportTypes';

import { useCallback, useMemo } from 'react';
import { useChartDataDisplay, useGetUnitSystem } from 'hooks/helperHooks';
import { Tooltip, Typography } from '@trackman/web-shared-components';
import { pitchTypeGroupToColor, pitchTypeToColor } from 'utilities/colorHelpers';
import { pitchTypeGroups, pitchTypes } from 'utilities/constants';

type Props = {
  pitchTypeRanges: PitchTypeRangesValueObject[] | undefined;
  isLoading: boolean;
  selectedMeasurementOption: PitchTypeRangesMeasurementOptions;
  seePitchesBy: PitchGrouping;
};

type RangeSettings = {
  min: number;
  max: number;
  step: number;
};

const imperialRangeSettings = new Map<PitchTypeRangesMeasurementOptions, RangeSettings>([
  ['Velocity', { min: 50, max: 110, step: 10 }],
  ['SpinRate', { min: 1000, max: 3000, step: 250 }],
  ['InducedVerticalBreak', { min: -25, max: 25, step: 5 }],
  ['HorizontalBreak', { min: -25, max: 25, step: 5 }],
]);

const metricRangeSettings = new Map<PitchTypeRangesMeasurementOptions, RangeSettings>([
  ['Velocity', { min: 80, max: 180, step: 10 }],
  ['SpinRate', { min: 1000, max: 3000, step: 250 }],
  ['InducedVerticalBreak', { min: -60, max: 60, step: 10 }],
  ['HorizontalBreak', { min: -60, max: 60, step: 10 }],
]);

const PitchTypeRanges = ({ pitchTypeRanges, isLoading, selectedMeasurementOption, seePitchesBy }: Props) => {
  const { t } = useTranslation();
  const unitSystem = useGetUnitSystem();

  const rangeSettings = useMemo(
    () => (unitSystem === 'Imperial' ? imperialRangeSettings : metricRangeSettings).get(selectedMeasurementOption) as RangeSettings,
    [selectedMeasurementOption]
  );

  const simulateChartData = useCallback(
    (isLoading: boolean) => {
      const simulatedChartData: PitchTypeRangesValueObject[] = [];

      const pitchTypeLabels = seePitchesBy === 'Types' ? pitchTypes : pitchTypeGroups;
      for (const pitchType of pitchTypeLabels.filter((pitchType) => pitchType !== 'Undefined')) {
        if (!isLoading) {
          simulatedChartData.push({ pitchType, minValue: 0, avgValue: 0, maxValue: 0 });
          continue;
        }

        const rangeSpan = rangeSettings.max - rangeSettings.min;
        const minValue = rangeSettings.min + Math.random() * rangeSpan * 0.8;
        const maxValue = minValue + Math.random() * minValue * 0.1;
        const avgValue = (minValue + maxValue) * 0.5 + (Math.random() - 0.5) * 0.5 * (maxValue - minValue);
        simulatedChartData.push({ pitchType, minValue, avgValue, maxValue });
      }

      return simulatedChartData;
    },
    [rangeSettings]
  );

  let chartDataDisplay = useChartDataDisplay(pitchTypeRanges, simulateChartData, isLoading);
  if (chartDataDisplay.length === 0) {
    chartDataDisplay = simulateChartData(false);
  }

  const minValue = useMemo(() => Math.min(...chartDataDisplay.map((ptr) => ptr.minValue)), [chartDataDisplay]);
  const maxValue = useMemo(() => Math.max(...chartDataDisplay.map((ptr) => ptr.maxValue)), [chartDataDisplay]);
  const stepValue = useMemo(() => (maxValue - minValue) / 7, [maxValue, minValue]);
  const stepScale = useMemo(() => (stepValue < 1 ? 1 : stepValue < 10 ? 10 : stepValue < 100 ? 100 : 1000), [stepValue]);
  const stepValueRounded = useMemo(() => Math.ceil(stepValue / stepScale) * stepScale, [stepValue, stepScale]);
  const rangeMin = useMemo(() => Math.floor(minValue / stepValueRounded) * stepValueRounded, [minValue, stepValueRounded]);
  const rangeMax = useMemo(() => Math.ceil(maxValue / stepValueRounded) * stepValueRounded, [maxValue, stepValueRounded]);
  const rangeSpan = useMemo(() => rangeMax - rangeMin, [rangeMax, rangeMin]);
  const rangeSteps = useMemo(() => {
    const steps: number[] = [];
    for (let i = rangeMin; i <= rangeMax; i += stepValueRounded) {
      steps.push(i);
    }
    return steps;
  }, [rangeMin, rangeMax, stepValueRounded]);

  const sortedPitchTypeRanges = useMemo(() => chartDataDisplay.sort((a, b) => a.pitchType.localeCompare(b.pitchType)), [chartDataDisplay]);

  return (
    <div className='PitchTypeRanges-container'>
      <div className='flex-row gap-2 flex-grow'>
        <div className='flex-column justify-around'>
          {sortedPitchTypeRanges.map((pitchTypeRange) => (
            <Typography component='div' key={pitchTypeRange.pitchType}>
              {t(pitchTypeRange.pitchType)}
            </Typography>
          ))}
        </div>
        <div className='PitchTypeRanges-chart'>
          <div className='PitchTypeRanges-ranges'>
            {sortedPitchTypeRanges.map((pitchTypeRange) => (
              <div className='PitchTypeRanges-range-bg' key={pitchTypeRange.pitchType}>
                <Tooltip
                  title={`Min: ${pitchTypeRange.minValue}, Avg: ${pitchTypeRange.avgValue}, Max: ${pitchTypeRange.maxValue}`}
                  placement='bottom'
                  arrow
                >
                  <div
                    className='PitchTypeRanges-range-fill'
                    style={{
                      left: `${((pitchTypeRange.minValue - rangeMin) / rangeSpan) * 100}%`,
                      right: `${((rangeMax - pitchTypeRange.maxValue) / rangeSpan) * 100}%`,
                      backgroundColor: pitchTypeToColor[pitchTypeRange.pitchType] ?? pitchTypeGroupToColor[pitchTypeRange.pitchType],
                    }}
                  ></div>
                </Tooltip>
                <div
                  className='PitchTypeRanges-range-avg'
                  style={{
                    left: `${((pitchTypeRange.avgValue - rangeMin) / rangeSpan) * 100}%`,
                  }}
                ></div>
              </div>
            ))}
          </div>
          <div className='PitchTypeRanges-range-steps'>
            {rangeSteps.map((value) => (
              <div className='PitchTypeRanges-range-step' key={value}>
                <Typography className='PitchTypeRanges-range-step-value' component='div' key={value}>
                  {value}
                </Typography>
              </div>
            ))}
          </div>
        </div>
        <div className='flex-column justify-around'>
          {sortedPitchTypeRanges.map((pitchTypeRange) => (
            <Typography component='div' key={pitchTypeRange.pitchType}>
              {Math.round(pitchTypeRange.avgValue) || '-'}
              {selectedMeasurementOption === 'Velocity' ? (unitSystem === 'Metric' ? ' kph' : ' mph') : ''}
            </Typography>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PitchTypeRanges;
